import { useCallback, useMemo, useState } from "react";
import { Views, type View, type ToolbarProps, type EventPropGetter } from "react-big-calendar";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { formatIsoDate } from "@/utils/date-format";
import { ResourceBigCalendarView } from "@/components/resource-big-calendar/ResourceBigCalendarView";
import { parseCalendarViewFromSearchParam } from "@/components/resource-big-calendar/resourceCalendarTimeGridDefaults";
import { useSyncResourceCalendarSearchParams } from "@/components/resource-big-calendar/useSyncResourceCalendarSearchParams";
import { hallBookingEventShellStyle } from "./hall-calendar/hall-calendar-colors";
import { HallBookingsCalendarToolbarWithLegend } from "./hall-calendar/HallBookingsCalendarToolbarWithLegend";
import { HallBookingCalendarEventComponent } from "./hall-calendar/HallBookingCalendarEventComponent";
import { HallBookingMonthDateHeader } from "./hall-calendar/HallBookingMonthDateHeader";
import { HallBookingDetailsDialog } from "./hall-calendar/HallBookingDetailsDialog";
import { useHallBookingsCalendarEvents } from "./hall-calendar/use-hall-bookings-calendar-events";
import type { HallBookingCalendarEvent, HallBookingResource } from "./hall-calendar/types";

export const HallBookingsCalendarView = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"), { noSsr: true });

  const [searchParams, setSearchParams] = useSearchParams();
  useSyncResourceCalendarSearchParams(isMobile);

  // URL is the single source of truth for view and date.
  const view: View = parseCalendarViewFromSearchParam(searchParams.get("view"), isMobile);
  const dateParam = searchParams.get("date");
  const date: Dayjs = dateParam && dayjs(dateParam).isValid() ? dayjs(dateParam) : dayjs();

  const [selectedEvent, setSelectedEvent] = useState<HallBookingResource | null>(null);
  const [showModal, setShowModal] = useState(false);

  const events = useHallBookingsCalendarEvents(view, date);

  const handleViewChange = useCallback(
    (newView: View) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", String(newView));
        return next;
      });
    },
    [setSearchParams]
  );

  const handleDateChange = useCallback(
    (newDate: Dayjs) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("date", formatIsoDate(newDate));
        return next;
      });
    },
    [setSearchParams]
  );

  const handleSelectEvent = (event: HallBookingCalendarEvent) => {
    if (event?.resource && !event?.isBlockedDate) {
      setSelectedEvent(event.resource);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const renderToolbar = useCallback(
    (toolbarProps: ToolbarProps) => (
      <HallBookingsCalendarToolbarWithLegend
        {...toolbarProps}
        events={events}
        selectedDate={date}
        setSelectedDate={handleDateChange}
      />
    ),
    [events, date, handleDateChange]
  );

  const eventPropGetter = useMemo(
    () =>
      ((event: HallBookingCalendarEvent) => ({
        style: {
          ...hallBookingEventShellStyle(muiTheme, event),
          ...(view === Views.MONTH
            ? {
                padding: "1px 3px",
                fontSize: "0.7rem",
                lineHeight: 1.15,
                minHeight: 0,
              }
            : {
                padding: "3px 5px",
                fontSize: "0.75rem",
                lineHeight: 1.2,
              }),
        },
      })) as EventPropGetter<HallBookingCalendarEvent>,
    [muiTheme, view]
  );

  return (
    <ResourceBigCalendarView<HallBookingCalendarEvent>
      events={events}
      view={view}
      onViewChange={handleViewChange}
      date={date}
      onDateChange={handleDateChange}
      onSelectEvent={handleSelectEvent}
      EventComponent={HallBookingCalendarEventComponent}
      renderToolbar={renderToolbar}
      monthDateHeader={HallBookingMonthDateHeader}
      eventPropGetter={eventPropGetter}
    >
      <HallBookingDetailsDialog
        open={showModal}
        onClose={handleCloseModal}
        selectedEvent={selectedEvent}
      />
    </ResourceBigCalendarView>
  );
};

export default HallBookingsCalendarView;
